import ExamUrgencyAlertMixin from '@/shared/mixins/exam-urgency-alert.mixin'
import DateMixin from '@/shared/mixins/date.mixin'
import RoleMixin from '@/shared/mixins/role.mixin'
import AuthMixin from '@/shared/mixins/auth.mixin'
import CONSTANTS from '@/shared/constants/constants'
import RadioExamsMixin from '@/shared/mixins/radio-report.mixin'

const VIDEO_ROOM_STORAGE_KEY = 'tele-ecg-video-room'
const VIDEO_OUTCOME_DONE_KEY = 'tele-ecg-video-outcome-done'
const VIDEO_OUTCOME_MAX_LENGTH = 2000
const VIDEO_OUTCOME_FALLBACK_MIN_LENGTH = 1
const VIDEO_ROOM_GONE_STATUSES = [404, 409, 410]

function readInferenceMinLength (source) {
  if (!source) return null
  const raw = source.minSizePhysicianInference != null
    ? source.minSizePhysicianInference
    : source.MinSizePhysicianInference
  if (raw == null || raw === '') return null
  const value = Number(raw)
  if (!Number.isInteger(value) || value < 0) return null
  return value
}

function readFinishedVideoCallExamId (examId) {
  try {
    const stored = sessionStorage.getItem(VIDEO_OUTCOME_DONE_KEY)
    if (!stored) return null
    if (examId != null && String(stored) !== String(examId)) {
      sessionStorage.removeItem(VIDEO_OUTCOME_DONE_KEY)
      return null
    }
    return stored
  } catch (error) {
    return null
  }
}

function readStoredVideoRoom () {
  try {
    localStorage.removeItem(VIDEO_ROOM_STORAGE_KEY)
    const raw = sessionStorage.getItem(VIDEO_ROOM_STORAGE_KEY)
    if (!raw) return null
    const stored = JSON.parse(raw)
    if (!stored || !stored.conferenceId || !stored.endToken) {
      sessionStorage.removeItem(VIDEO_ROOM_STORAGE_KEY)
      return null
    }
    return stored
  } catch (error) {
    return null
  }
}

export default {
  name: 'exam-details-alert',
  props: {
    exam: {
      type: Object,
      required: true
    },
    showVideoCall: {
      type: Boolean,
      default: false
    }
  },
  mixins: [
    DateMixin,
    ExamUrgencyAlertMixin,
    RoleMixin,
    AuthMixin,
    RadioExamsMixin
  ],
  data () {
    return {
      placeholder: this.$i18n.t('inform_alert_reason'),
      informations: [],
      roles: [],
      STATUS: CONSTANTS.STATUS,
      toggle: false,
      maxLength: 1000,
      message: null,
      urgencyTooltip: this.$i18n.t('tooltips.already_has_urgency_alert'),
      hasAlert: false,
      videoCallOpen: false,
      videoCallUrl: '',
      videoCallLoading: false,
      videoRoom: readStoredVideoRoom(),
      videoOutcomeOpen: false,
      videoOutcomeText: '',
      videoOutcomeSaving: false,
      videoOutcomeMaxLength: VIDEO_OUTCOME_MAX_LENGTH,
      videoCallDisabledExamId: readFinishedVideoCallExamId(this.exam && this.exam.examId)
    }
  },
  watch: {
    'exam.examId' (examId) {
      if (examId == null || this.videoCallDisabledExamId == null) return
      if (String(examId) === String(this.videoCallDisabledExamId)) return
      this.videoCallDisabledExamId = null
      sessionStorage.removeItem(VIDEO_OUTCOME_DONE_KEY)
    }
  },
  mounted () {
    this.$nextTick(() => {
      this.appendToBody(this.$refs.videoCallLayer)
      if (this.showVideoCall && this.videoRoom && !this.videoCallOpen) {
        this.openVideoOutcome()
      }
    })
  },
  beforeDestroy () {
    this.removeFromBody(this.$refs.videoCallLayer)
    this.removeFromBody(this.$refs.videoOutcomeLayer)
  },

  computed: {
    canCallSource () {
      const exam = this.exam || {}
      return Boolean(exam.canCallSource || exam.CanCallSource)
    },
    showVideoCallButton () {
      return this.showVideoCall && this.canCallSource
    },
    videoOutcomeMinLength () {
      const minLength = readInferenceMinLength(this.videoRoom)
      return minLength != null ? minLength : VIDEO_OUTCOME_FALLBACK_MIN_LENGTH
    },
    videoOutcomeExamId () {
      return (this.videoRoom && this.videoRoom.examId) || this.exam.examId
    },
    canSaveVideoOutcome () {
      const text = (this.videoOutcomeText || '').trim()
      return text.length > 0 && text.length >= this.videoOutcomeMinLength && text.length <= this.videoOutcomeMaxLength
    },
    videoCallFinished () {
      return this.videoCallDisabledExamId != null
        && String(this.videoCallDisabledExamId) === String(this.exam && this.exam.examId)
    },
    tooltipMessage () {
      if (this.exam.hasUrgencyAlert) {
        return this.$i18n.t('tooltips.already_has_urgency_alert')
      }
      if (this.hasAlert) {
        return this.$i18n.t('tooltips.already_has_urgency_alert')
      }
      return undefined
    }
  },
  methods: {
    async clickSendUrgencyAlert () {
      this.$root.$emit('showLoading')
      try {
        await this.sendUrgencyAlert({
          examId: this.exam.examId,
          description: this.message
        })
        this.exam.hasUrgencyAlert = true
        this.message = null
        this.$emit('rerender')
        this.hide()
      } finally {
        this.$root.$emit('hideLoading')
        this.$root.$emit('toast', { message: this.$i18n.t('feedback.alert_sent'), type: 'info' })
      }
    },
    focus () {
      this.placeholder = ''
    },
    blur () {
      this.placeholder = this.$i18n.t('inform_alert_reason')
    },
    hide () {
      this.toggle = false
    },
    appendToBody (layer) {
      if (layer && layer.parentNode !== document.body) {
        document.body.appendChild(layer)
      }
    },
    removeFromBody (layer) {
      if (layer && layer.parentNode) {
        layer.parentNode.removeChild(layer)
      }
    },
    saveVideoRoom (room) {
      const stored = {
        examId: this.exam.examId,
        conferenceId: room.conferenceId,
        endToken: room.endToken
      }
      const minLength = readInferenceMinLength(room)
      if (minLength != null) stored.minSizePhysicianInference = minLength
      sessionStorage.setItem(VIDEO_ROOM_STORAGE_KEY, JSON.stringify(stored))
      this.videoRoom = stored
    },
    clearVideoRoom () {
      localStorage.removeItem(VIDEO_ROOM_STORAGE_KEY)
      sessionStorage.removeItem(VIDEO_ROOM_STORAGE_KEY)
      this.videoRoom = null
    },
    openVideoOutcome () {
      if (this.videoOutcomeOpen || !this.videoRoom) return
      this.videoOutcomeText = ''
      this.videoOutcomeOpen = true
      this.$nextTick(() => {
        this.appendToBody(this.$refs.videoOutcomeLayer)
        this.$root.$emit('videoCallSession', true)
      })
    },
    async clickVideoCall () {
      if (this.videoCallFinished || this.videoCallOpen || this.videoCallLoading || this.videoOutcomeOpen) return
      if (this.videoRoom) {
        this.openVideoOutcome()
        return
      }
      this.videoCallLoading = true
      this.$root.$emit('showLoading')
      try {
        const res = await this.requestPost('/core/v2/External/RequestOpeningRoom', {
          examId: this.exam.examId
        })
        const room = res && res.data && res.data.data
        if (!room || !room.conferenceLink || !room.conferenceId || !room.endToken) {
          this.showToast(this.$t('video_room_error'))
          return
        }
        this.saveVideoRoom(room)
        this.videoCallUrl = room.conferenceLink
        this.videoCallOpen = true
        this.$root.$emit('videoCallSession', true)
      } finally {
        this.videoCallLoading = false
        this.$root.$emit('hideLoading')
      }
    },
    closeVideoCall () {
      if (document.fullscreenElement) {
        document.exitFullscreen()
      }
      this.videoCallOpen = false
      this.videoCallUrl = ''
      if (!this.videoRoom) {
        this.$root.$emit('videoCallSession', false)
        return
      }
      this.openVideoOutcome()
    },
    async saveVideoOutcome () {
      if (!this.canSaveVideoOutcome || this.videoOutcomeSaving) return
      const room = this.videoRoom
      if (!room || !room.conferenceId || !room.endToken) return
      this.videoOutcomeSaving = true
      this.$root.$emit('showLoading')
      this.bypass = true
      try {
        const res = await this.requestPost('/core/v2/External/RequestCloseRoom', {
          examId: room.examId || this.exam.examId,
          conferenceId: room.conferenceId,
          endToken: room.endToken,
          inference: this.videoOutcomeText.trim()
        })
        if (res && res.isAxiosError) {
          const status = res.response && res.response.status
          if (VIDEO_ROOM_GONE_STATUSES.includes(status)) {
            this.finishVideoOutcome()
            return
          }
          this.showToast(this.getRequestErrorMessage(res) || this.$t('video_outcome_error'))
          return
        }
        this.markVideoCallFinished(room.examId || this.exam.examId)
        this.finishVideoOutcome()
      } catch (error) {
        // 401 já é tratado pelo interceptor do client.
      } finally {
        this.bypass = false
        this.videoOutcomeSaving = false
        this.$root.$emit('hideLoading')
      }
    },
    markVideoCallFinished (examId) {
      if (examId == null) return
      this.videoCallDisabledExamId = examId
      sessionStorage.setItem(VIDEO_OUTCOME_DONE_KEY, String(examId))
    },
    finishVideoOutcome () {
      this.clearVideoRoom()
      this.videoOutcomeOpen = false
      this.videoOutcomeText = ''
      this.$root.$emit('videoCallSession', false)
    },
    getRequestErrorMessage (error) {
      const data = error && error.response && error.response.data
      if (!data) return null
      if (data.message) return data.message
      const errors = data.errors
      if (Array.isArray(errors) && errors.length) {
        return errors[0].message || errors[0].Message || null
      }
      return typeof errors === 'string' ? errors : null
    },
    toggleVideoCallExpand () {
      const frame = this.$refs.videoCallFrame
      if (!frame) return
      if (document.fullscreenElement) {
        document.exitFullscreen()
        return
      }
      frame.requestFullscreen()
    }
  }
}
